// Validation at the edges: anything read from storage or produced by an
// importer passes through here before it becomes a typed record.
import type { GameRecord, GameResult, GameSource, GameSummary, PlayerRef } from '@chess/api-model';

const RESULTS: readonly GameResult[] = ['1-0', '0-1', '1/2-1/2', '*'];
const SOURCES: readonly GameSource[] = ['chess.com', 'lichess', 'local'];

export class SchemaError extends Error {}

function fail(what: string): never {
  throw new SchemaError(`invalid game record: ${what}`);
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function str(o: Record<string, unknown>, key: string): string {
  const v = o[key];
  if (typeof v !== 'string' || v.length === 0) fail(`${key} must be a non-empty string`);
  return v;
}

function optStr(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  if (v === undefined) return undefined;
  if (typeof v !== 'string') fail(`${key} must be a string`);
  return v;
}

function isoDate(o: Record<string, unknown>, key: string): string {
  const v = str(o, key);
  if (Number.isNaN(Date.parse(v))) fail(`${key} must be an ISO date`);
  return v;
}

function player(o: Record<string, unknown>, key: string): PlayerRef {
  const v = o[key];
  if (!isObject(v)) fail(`${key} must be a player`);
  const name = str(v, 'name');
  const rating = v.rating;
  if (rating !== undefined && typeof rating !== 'number') fail(`${key}.rating must be a number`);
  return rating === undefined ? { name } : { name, rating };
}

function oneOf<T extends string>(o: Record<string, unknown>, key: string, allowed: readonly T[]): T {
  const v = o[key];
  if (typeof v !== 'string' || !(allowed as readonly string[]).includes(v)) fail(`${key} must be one of ${allowed.join(', ')}`);
  return v as T;
}

/** Parses an unknown value into a GameSummary, or throws SchemaError. */
export function parseGameSummary(value: unknown): GameSummary {
  if (!isObject(value)) fail('not an object');
  const summary: GameSummary = {
    id: str(value, 'id'),
    owner: str(value, 'owner'),
    white: player(value, 'white'),
    black: player(value, 'black'),
    result: oneOf(value, 'result', RESULTS),
    playedAt: isoDate(value, 'playedAt'),
    importedAt: isoDate(value, 'importedAt'),
    source: oneOf(value, 'source', SOURCES),
  };
  const url = optStr(value, 'url');
  if (url !== undefined) summary.url = url;
  const timeControl = optStr(value, 'timeControl');
  if (timeControl !== undefined) summary.timeControl = timeControl;
  return summary;
}

/** Parses an unknown value into a GameRecord, or throws SchemaError. */
export function parseGameRecord(value: unknown): GameRecord {
  if (!isObject(value)) fail('not an object');
  if (value.schemaVersion !== 1) fail(`unsupported schemaVersion ${String(value.schemaVersion)}`);
  const summary = parseGameSummary(value);
  return { schemaVersion: 1, ...summary, pgn: str(value, 'pgn') };
}

/** The listing shape of a record. */
export function summarize(record: GameRecord): GameSummary {
  const { pgn: _pgn, schemaVersion: _v, ...summary } = record;
  return summary;
}
